/**
 * The actions a bar could not fit inline, in a tray of their own.
 *
 * It exists because the alternative was worse: the bar used to append its
 * overflow onto the primary's `alternates`, so a vaccine-booking link ended up
 * inside a disclosure whose accessible name was `More ways to navigate`. A
 * route and a booking are different intents, and a tray is a claim that
 * everything inside it is one intent spelled several ways. Overflow is not
 * that — it is a set of distinct things to do that ran out of room — so it gets
 * its own control and its own name.
 *
 * Nothing a pillar declared is dropped. That is the whole reason the overflow
 * exists rather than a slice.
 */
"use client";
import { useCallback, useId, useState, type ReactElement } from "react";
import { IconChevronDown } from "@tabler/icons-react";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import { CRC_R_PILL, CRC_TEXT, CRC_TOUCH_TARGET } from "./cresultcard.tokens";
import { useCresultcardViewport } from "./cresultcard.viewport";
import {
  CresultcardActionTone,
  type CresultcardActionSpec,
} from "./cresultcard.types";
import { CresultcardAction } from "./CresultcardAction";
import { CresultcardShutter } from "./CresultcardShutter";

const CHEVRON_SIZE = 13;
const CHEVRON_STROKE = 1.5;
const TRAY_GAP = 6;
const TRAY_ORIGIN = "left top";
const OVERFLOW_LABEL = "More actions";
const TOGGLE_PADDING = "6px 12px";
const TOGGLE_TEXT_SIZE = 11;
const TOGGLE_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/ActionOverflow/Trigger",
  "data-crc-control": true,
};

interface CresultcardActionOverflowProps {
  readonly actions: readonly CresultcardActionSpec[];
  readonly fullWidth: boolean;
}

export function CresultcardActionOverflow({
  actions,
  fullWidth,
}: CresultcardActionOverflowProps): ReactElement {
  const [open, setOpen] = useState(false);
  const trayId = useId();
  const { isMobile } = useCresultcardViewport();
  const toggle = useCallback((): void => {
    setOpen((current) => !current);
  }, []);

  return (
    <div
      data-el="Cresultcard/ActionOverflow/Root"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: fullWidth ? "stretch" : "flex-start",
        gap: TRAY_GAP,
        minWidth: 0,
        width: fullWidth ? "100%" : undefined,
      }}
    >
      <Cbutton
        ariaLabel={OVERFLOW_LABEL}
        containerStyle={{
          color: CRC_TEXT,
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.1)",
          minHeight: isMobile ? CRC_TOUCH_TARGET : undefined,
          width: fullWidth ? "100%" : undefined,
        }}
        contentStyle={{
          display: "inline-flex",
          alignItems: "center",
          gap: TRAY_GAP,
          fontSize: TOGGLE_TEXT_SIZE,
          fontWeight: 700,
          letterSpacing: "0.01em",
          whiteSpace: "nowrap",
        }}
        disableClickMotion
        onClick={toggle}
        padding={TOGGLE_PADDING}
        preset={ButtonPreset.Ghost}
        radius={CRC_R_PILL}
        rootAttributes={{
          ...TOGGLE_ROOT_ATTRIBUTES,
          "aria-controls": trayId,
          "aria-expanded": open,
        }}
        style={{ width: fullWidth ? "100%" : undefined }}
      >
        {OVERFLOW_LABEL}
        <span
          className="cresultcard__chevron"
          data-crc-expanded={open}
          style={{ display: "inline-flex" }}
        >
          <IconChevronDown size={CHEVRON_SIZE} stroke={CHEVRON_STROKE} />
        </span>
      </Cbutton>

      <CresultcardShutter
        id={trayId}
        open={open}
        transformOrigin={TRAY_ORIGIN}
      >
        <div
          data-el="Cresultcard/ActionOverflow/Actions"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: TRAY_GAP,
            paddingTop: TRAY_GAP,
          }}
        >
          {actions.map((action) => (
            <CresultcardAction
              compact
              href={action.href}
              icon={action.icon}
              key={action.label}
              label={action.label}
              onPress={action.onPress}
              tone={CresultcardActionTone.Quiet}
              value={action.value == null ? undefined : String(action.value)}
            />
          ))}
        </div>
      </CresultcardShutter>
    </div>
  );
}
